/**
 * Pure date/time converter for the Resources Maintenance modal submit contract.
 *
 * Extracted from MaintenanceModal so the epoch/wall-clock contract can be unit
 * tested as a pure function (mirrors calendar-month/utils/dateTime.js). No
 * React / HeroUI imports here — keep it dependency-light.
 *
 * @module maintenanceDateTime
 */
import { CalendarDateTime, fromDate, getLocalTimeZone } from '@internationalized/date';

/**
 * Convert a `CalendarDateTime` (or the AppDatePicker plain `{year,month,day,...}`
 * object) to Unix epoch **seconds** interpreted in the RESOURCE/location timezone.
 *
 * Mirrors `cdtToEpoch` from calendar-month/utils/dateTime.js: we rebuild a real
 * `@internationalized/date` `CalendarDateTime` and call its `.toDate(tz)`. The
 * AppDatePicker plain object's own `.toDate()` ignores the tz argument and
 * returns a browser-local Date (AppDatePicker.js:62-77), so the picked
 * wall-clock would silently shift when the browser tz differs from the resource
 * tz. Reconstructing here anchors the wall-clock to `timezoneId`.
 *
 * The API adapter (`formatMaintenanceDate`) expects epoch seconds; a
 * `Date.toString()` string produced `NaN → '' →` backend HTTP 422 (HW-957).
 *
 * @param {{year:number,month:number,day:number,hour?:number,minute?:number,second?:number}|null} cdt
 * @param {string} [timezoneId] IANA tz (e.g. "America/Denver"); falls back to browser tz.
 * @returns {number|null} Unix epoch seconds, or null for falsy/invalid input.
 */
export const calendarDateTimeToTimestamp = (cdt, timezoneId) => {
	if (!cdt) return null;
	const tz = timezoneId || getLocalTimeZone();
	const real = new CalendarDateTime(
		cdt.year,
		cdt.month,
		cdt.day,
		cdt.hour ?? 0,
		cdt.minute ?? 0,
		cdt.second ?? 0
	);
	return Math.floor(real.toDate(tz).getTime() / 1000);
};

/**
 * Inverse of {@link calendarDateTimeToTimestamp}: hydrate a stored epoch (or
 * date string) into a date value whose wall-clock fields are expressed in the
 * RESOURCE/location timezone — so the picker shows the same wall-clock that was
 * stored, and submit round-trips back to the identical epoch.
 *
 * MUST use the SAME `timezoneId` that submit uses. If it rendered in the browser
 * tz (`getLocalTimeZone()`) while submit rebuilds the epoch in the resource tz,
 * an edited record double-shifts whenever browser tz ≠ resource tz (HW-957
 * edit-path regression).
 *
 * @param {number|string|null} value  Unix epoch seconds (number or numeric
 *   string > 1e9) or a parseable date string.
 * @param {string} [timezoneId] IANA tz; falls back to browser tz.
 * @returns {import('@internationalized/date').ZonedDateTime|null} A value with
 *   `{year,month,day,hour,minute,second}` in `timezoneId`, or null for falsy input.
 */
export const timestampToCalendarDateTime = (value, timezoneId) => {
	if (!value) return null;
	let date;
	if (typeof value === 'number') {
		// Unix timestamp in seconds
		date = new Date(value * 1000);
	} else if (typeof value === 'string') {
		// Could be a Unix timestamp as string or a date string
		const num = Number(value);
		if (!isNaN(num) && num > 1000000000) {
			date = new Date(num * 1000);
		} else {
			date = new Date(value);
		}
	} else {
		return null;
	}
	return fromDate(date, timezoneId || getLocalTimeZone());
};
